import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton } from '@parrot-software/design-system';
import { ReportBlockContainer } from '@reports/presentation/components/base/ReportBlockContainer';
import { ReportEmptyState } from '@reports/presentation/components/base/ReportEmptyState';
import { ReportErrorState } from '@reports/presentation/components/base/ReportErrorState';

export interface ReportChartStatesProps {
  width?: 'sm' | 'md' | 'lg' | 'xl';
  title?: string;
  description?: string;
  state: 'loading' | 'error' | 'empty';
  onRetry?: () => void;
  errorMessage?: string;
}

/**
 * ReportChartStates - Unified state component for chart blocks
 *
 * Handles loading, error, and empty states with consistent styling.
 * Memoized to prevent unnecessary re-renders.
 *
 * @param {ReportChartStatesProps} props - Component properties
 * @param {'sm' | 'md' | 'lg' | 'xl'} [props.width='lg'] - Block width
 * @param {string} [props.title] - Block title translation key
 * @param {string} [props.description] - Block description translation key
 * @param {'loading' | 'error' | 'empty'} props.state - Current state to display
 * @param {string} [props.errorMessage] - Custom error message translation key
 * @param {string} [props.emptyMessage] - Custom empty message translation key
 * @returns {React.ReactElement} State component
 *
 * @example
 * <ReportChartStates
 *   width="md"
 *   title="reportSales:charts.title"
 *   state="loading"
 * />
 */
const ReportChartStatesComponent = ({
  width = 'lg',
  title,
  description,
  state,
  onRetry,
  errorMessage = 'reports:messages.errorLoadingData',
}: ReportChartStatesProps) => {
  const { t } = useTranslation(['reports', 'reportSales']);

  const renderContent = () => {
    switch (state) {
      case 'loading':
        return <Skeleton className=" h-full w-full bg-(--base-accent-bg-accent50)" />;

      case 'error':
        return <ReportErrorState message={errorMessage} onRetry={onRetry} />;

      case 'empty':
        return <ReportEmptyState />;

      default:
        return null;
    }
  };

  return (
    <ReportBlockContainer width={width}>
      <Card className="h-[364px]">
        {title && (
          <CardHeader>
            <CardTitle className="text-base font-medium">{t(title)}</CardTitle>
            {description && <CardDescription className="mt-0!">{t(description)}</CardDescription>}
          </CardHeader>
        )}

        <CardContent className="flex items-center justify-center h-[240px] py-0">{renderContent()}</CardContent>
      </Card>
    </ReportBlockContainer>
  );
};

/**
 * Memoized version of ReportChartStates
 *
 * Prevents re-renders when props haven't changed, improving performance.
 */
export const ReportChartStates = memo(ReportChartStatesComponent);
